import { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useConfig } from '../context/ConfigContext';
import Footer from '../components/layout/Footer';
import api from '../utils/api';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const { FiMail, FiMessageSquare, FiUser, FiSend, FiMapPin, FiPhone, FiHeart } = FiIcons;

const Contact = () => {
    const { config } = useConfig();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            if (!executeRecaptcha) {
                toast.error('reCAPTCHA not ready');
                return;
            }

             const recaptchaToken = await executeRecaptcha('contact_form');

            const payload = {
                ...formData,
                recaptcha_token: recaptchaToken, // ✅ send token to backend
            };

            const response = await api.post('/contacts', payload);

            // Axios success = status 2xx
            if (response.status !== 201 && response.status !== 200) {
                throw new Error('Request failed');
            }

            toast.success("Message sent successfully! We'll get back to you soon.");
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            toast.error('Failed to send message. Please try again.');
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const contactInfo = [
        {
            icon: FiMail,
            title: 'Email Us',
            description: 'Send us an email anytime',
            value: config.contactEmail || 'contact@islam101.com',
            link: `mailto:${config.contactEmail || 'contact@islam101.com'}`
        },
        {
            icon: FiPhone,
            title: 'Call Us',
            description: 'Mon-Fri from 9am to 6pm',
            value: config.supportPhone || '+1 (555) 123-4567',
            link: `tel:${(config.supportPhone || '+1 (555) 123-4567').replace(/[^0-9+]/g, '')}`
        },
        {
            icon: FiMapPin,
            title: 'Visit Us',
            description: 'Come say hello at our office',
            value: config.address || '123 Islamic Center St, City, State 12345',
            link: '#'
        }
    ];

    return (
        <div className="min-h-screen pt-12">
            <div className="max-w-6xl mx-auto pb-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-12"
                >
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        Contact Us
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        Have questions about {config.platformName}? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-12 gap-12">
                    {/* Contact Form */}
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8"
                        >
                            <div className="flex items-center space-x-3 mb-6">
                                <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-emerald-600" />
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Send us a Message
                                </h2>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Full Name *
                                        </label>
                                        <div className="relative">
                                            <SafeIcon
                                                icon={FiUser}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                                            />
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Your full name"
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Email Address *
                                        </label>
                                        <div className="relative">
                                            <SafeIcon
                                                icon={FiMail}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                                            />
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="your@email.com"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Subject */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Subject *
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        placeholder="What is this about?"
                                    />
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Message *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows={6}
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                                        placeholder="Tell us how we can help you..."
                                    />
                                </div>

                                {/* Submit Button */}
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full flex items-center justify-center space-x-2 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-semibold rounded-lg transition-colors duration-200"
                                >
                                    {isLoading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <SafeIcon icon={FiSend} className="w-4 h-4" />
                                            <span>Send Message</span>
                                        </>
                                    )}
                                </motion.button>
                            </form>
                        </motion.div>
                    </div>

                    {/* Contact Information */}
                    <div className="lg:col-span-5 space-y-6">
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-8 text-white"
                        >
                            <h3 className="text-xl font-bold mb-6">Get in Touch</h3>
                            <p className="mb-8 opacity-90">
                                We're here to help you on your Islamic learning journey. Whether you have questions about our platform, need technical support, or want to provide feedback, we'd love to hear from you.
                            </p>

                            <div className="space-y-6">
                                {contactInfo.map((info, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 + index * 0.1 }}
                                        className="flex items-start space-x-4"
                                    >
                                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <SafeIcon icon={info.icon} className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold mb-1">{info.title}</h4>
                                            <p className="text-sm opacity-80 mb-1">{info.description}</p>
                                            {info.link && info.link !== '#' ? (
                                                <a
                                                    href={info.link}
                                                    className="text-sm font-medium hover:underline"
                                                >
                                                    {info.value}
                                                </a>
                                            ) : (
                                                <p className="text-sm font-medium">{info.value}</p>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Donation Card */}
                        {config.donationLink && (
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 }}
                                className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700/50 p-6"
                            >
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg text-amber-600 dark:text-amber-400">
                                        <SafeIcon icon={FiHeart} className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                        Support Our Mission
                                    </h3>
                                </div>

                                <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
                                    Your generous contributions help us maintain and improve {config.platformName}, keeping it free and accessible for everyone.
                                </p>

                                <motion.a
                                    href={config.donationLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full flex items-center justify-center space-x-2 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-amber-500/20"
                                >
                                    <SafeIcon icon={FiHeart} className="w-4 h-4" />
                                    <span>Donate Now</span>
                                </motion.a>
                            </motion.div>
                        )}

                        {/* Additional Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 }}
                            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
                        >
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                                Frequently Asked Questions
                            </h3>
                            <div className="space-y-4 text-sm">
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                        Is {config.platformName} free to use?
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Yes, our platform is completely free and accessible to everyone.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                        Can I download content for offline use?
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Yes, you can download selected Surahs, Duas, and books for offline reading.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                        How can I contribute content?
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Contact us through this form to discuss content contributions and collaborations.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Contact;